'use client';

import { c as _c } from "react/compiler-runtime";
import { useSubmitFeedback } from '@morfi/backend-sdk-v2/client';
import { useLocale, useTranslations } from 'next-intl';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { InputField } from '@/components/ui/input-field';
import { ModalFooter } from '@/components/ui/modal-footer';
import { SelectField, type SelectFieldOption } from '@/components/ui/select-field';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { TextareaField } from '@/components/ui/textarea-field';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { showToast } from '@/lib/ui/toast';

/**
 * Partner / investor lead-capture modal for the signed-out early-access
 * landing. Reuses the in-app feedback pipeline (`useSubmitFeedback` →
 * `POST /v1/feedback` → the team Slack channel) with lead-specific inputs: the
 * category + contact + note are composed into the feedback `message`, tagged
 * with `surface = '<kind>_inquiry'` for triage. No new backend — `/v1/feedback`
 * is public and already renders anonymous submissions.
 */
export type LeadKind = 'partner' | 'invest';
interface CategoryOption {
  value: string;
  label: string;
}

/** Category options per kind. Labels use LITERAL i18n keys (not template
 *  strings) so the unused-key checker sees every key as referenced. */
function categoryOptions(kind: LeadKind, t: (k: string) => string): CategoryOption[] {
  if (kind === 'partner') {
    return [{
      value: 'exchange',
      label: t('partner.categories.exchange')
    }, {
      value: 'market_maker',
      label: t('partner.categories.market_maker')
    }, {
      value: 'media',
      label: t('partner.categories.media')
    }, {
      value: 'integration',
      label: t('partner.categories.integration')
    }, {
      value: 'community',
      label: t('partner.categories.community')
    }, {
      value: 'other',
      label: t('partner.categories.other')
    }];
  }
  return [{
    value: 'angel',
    label: t('invest.categories.angel')
  }, {
    value: 'vc',
    label: t('invest.categories.vc')
  }, {
    value: 'strategic',
    label: t('invest.categories.strategic')
  }, {
    value: 'family_office',
    label: t('invest.categories.family_office')
  }, {
    value: 'other',
    label: t('invest.categories.other')
  }];
}

/** Header / placeholder copy per kind, via literal i18n keys (both branches
 *  present so the checker counts all keys as used). */
function kindCopy(kind: LeadKind, t: (k: string) => string): {
  title: string;
  description: string;
  messagePlaceholder: string;
} {
  return kind === 'partner' ? {
    title: t('partner.title'),
    description: t('partner.description'),
    messagePlaceholder: t('partner.messagePlaceholder')
  } : {
    title: t('invest.title'),
    description: t('invest.description'),
    messagePlaceholder: t('invest.messagePlaceholder')
  };
}
export function EarlyAccessLeadModal(t0) {
  const $ = _c(79);
  const {
    kind,
    onClose
  } = t0;
  const t = useTranslations("landing.lead");
  let t1;
  if ($[0] !== t) {
    t1 = key => t(key as Parameters<typeof t>[0]);
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const tk = t1;
  const locale = useLocale();
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      meta: {
        skipGlobalErrorHandler: true
      }
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const submit = useSubmitFeedback(t2);
  const options = categoryOptions(kind, tk);
  let t3;
  if ($[3] !== kind || $[4] !== tk) {
    t3 = kindCopy(kind, tk);
    $[3] = kind;
    $[4] = tk;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const copy = t3;
  const [category, setCategory] = useState(options[0]?.value ?? "other");
  const [contact, setContact] = useState("");
  const [message, setMessage] = useState("");
  const items = options.map(_temp);
  let t4;
  if ($[6] !== contact || $[7] !== submit.isPending) {
    t4 = contact.trim().length > 0 && !submit.isPending;
    $[6] = contact;
    $[7] = submit.isPending;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const canSubmit = t4;
  let t5;
  if ($[9] !== canSubmit || $[10] !== category || $[11] !== contact || $[12] !== kind || $[13] !== locale || $[14] !== message || $[15] !== onClose || $[16] !== options || $[17] !== submit || $[18] !== t) {
    t5 = function handleSubmit() {
      if (!canSubmit) {
        return;
      }
      const categoryLabel = options.find(o_0 => o_0.value === category)?.label ?? category;
      const note = message.trim();
      const composed = [kind === "partner" ? "\uD83E\uDD1D Partner inquiry" : "\uD83D\uDCB0 Investor inquiry", `Category: ${categoryLabel}`, `Contact: ${contact.trim()}`, "", note.length > 0 ? note : "\u2014"].join("\n");
      submit.mutate({
        type: "other",
        message: composed,
        context: {
          surface: `${kind}_inquiry`,
          route: "/early-access",
          locale
        }
      }, {
        onSuccess: () => {
          onEvent("feedback_submitted", {
            type: "other",
            surface: `${kind}_inquiry`
          });
          showToast({
            id: TOAST_IDS.FEEDBACK_RESULT,
            message: t("successToast"),
            variant: "success"
          });
          onClose();
        },
        onError: () => {
          showToast({
            id: TOAST_IDS.FEEDBACK_RESULT,
            message: t("errorToast"),
            variant: "error",
            duration: 8000
          });
        }
      });
    };
    $[9] = canSubmit;
    $[10] = category;
    $[11] = contact;
    $[12] = kind;
    $[13] = locale;
    $[14] = message;
    $[15] = onClose;
    $[16] = options;
    $[17] = submit;
    $[18] = t;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const handleSubmit = t5;
  let t6;
  if ($[20] !== onClose) {
    t6 = next => {
      if (!next) {
        onClose();
      }
    };
    $[20] = onClose;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const t7 = copy.title;
  const t8 = copy.description;
  const t9 = submit.isPending;
  let t10;
  if ($[22] !== t) {
    t10 = t("cancel");
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== onClose || $[25] !== submit.isPending || $[26] !== t10) {
    t11 = <AnimatedButton variant="secondary" onClick={onClose} disabled={t9}>{t10}</AnimatedButton>;
    $[24] = onClose;
    $[25] = submit.isPending;
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const t12 = !canSubmit;
  let t13;
  if ($[28] !== submit.isPending || $[29] !== t) {
    t13 = submit.isPending ? t("sending") : t("submit");
    $[28] = submit.isPending;
    $[29] = t;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== handleSubmit || $[32] !== submit.isPending || $[33] !== t12 || $[34] !== t13) {
    t14 = <AnimatedButton onClick={handleSubmit} disabled={t12} loading={submit.isPending}>{t13}</AnimatedButton>;
    $[31] = handleSubmit;
    $[32] = submit.isPending;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t11 || $[37] !== t14) {
    t15 = <ModalFooter>{t11}{t14}</ModalFooter>;
    $[36] = t11;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t) {
    t16 = t("categoryLabel");
    $[39] = t;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== category || $[42] !== items || $[43] !== setCategory || $[44] !== submit.isPending || $[45] !== t16) {
    t17 = <SelectField label={t16} value={category} onValueChange={setCategory} items={items} disabled={submit.isPending} />;
    $[41] = category;
    $[42] = items;
    $[43] = setCategory;
    $[44] = submit.isPending;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== t) {
    t18 = t("contactLabel");
    $[47] = t;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t) {
    t19 = t("contactPlaceholder");
    $[49] = t;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== setContact) {
    t20 = e => setContact(e.target.value);
    $[51] = setContact;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== contact || $[54] !== submit.isPending || $[55] !== t18 || $[56] !== t19 || $[57] !== t20) {
    t21 = <InputField label={t18} required={true} fullWidth={true} value={contact} placeholder={t19} disabled={submit.isPending} onChange={t20} />;
    $[53] = contact;
    $[54] = submit.isPending;
    $[55] = t18;
    $[56] = t19;
    $[57] = t20;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== t) {
    t22 = t("messageLabel");
    $[59] = t;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== setMessage) {
    t23 = e_0 => setMessage(e_0.target.value);
    $[61] = setMessage;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  let t24;
  if ($[63] !== copy.messagePlaceholder || $[64] !== message || $[65] !== submit.isPending || $[66] !== t22 || $[67] !== t23) {
    t24 = <TextareaField label={t22} value={message} rows={4} placeholder={copy.messagePlaceholder} disabled={submit.isPending} onChange={t23} />;
    $[63] = copy.messagePlaceholder;
    $[64] = message;
    $[65] = submit.isPending;
    $[66] = t22;
    $[67] = t23;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  let t25;
  if ($[69] !== t17 || $[70] !== t21 || $[71] !== t24) {
    t25 = <div className="flex flex-col gap-4 pb-2">{t17}{t21}{t24}</div>;
    $[69] = t17;
    $[70] = t21;
    $[71] = t24;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  let t26;
  if ($[73] !== copy.description || $[74] !== copy.title || $[75] !== t15 || $[76] !== t25 || $[77] !== t6) {
    t26 = <SilkSheet open={true} onOpenChange={t6} variant="detached" size="default" keyboard={true} title={t7} description={t8} footer={t15}>{t25}</SilkSheet>;
    $[73] = copy.description;
    $[74] = copy.title;
    $[75] = t15;
    $[76] = t25;
    $[77] = t6;
    $[78] = t26;
  } else {
    t26 = $[78];
  }
  return t26;
}
function _temp(o) {
  return {
    value: o.value,
    label: o.label
  };
}